import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DOMAIN } from '../redux/constants';

// One authenticated WebSocket per tab, shared by every app-scoped consumer.
//
// The backend destroys the upgrade once a user holds 5 sockets
// (backend/index.ts:313), and a destroyed upgrade looks exactly like a failed
// open to the client - which trips the permanent give-up after 5 failures
// below. Before this provider, every new app-scoped socket consumer brought
// that ceiling closer; the bell would silently stop reconnecting for the rest
// of the session with nothing surfaced to the user. Consumers now multiplex
// over a single connection instead.
//
// The connect/backoff/bfcache machinery here is moved verbatim from
// NotificationBell.js - it is the most battle-tested frontend infra in the
// repo and its behaviour is deliberately unchanged: exponential backoff
// 1s -> 30s, permanent give-up after 5 consecutive failed opens, bfcache
// reconnect on pageshow/persisted, and re-arm on visibilitychange/online.
//
// Page-scoped sockets (Memory, MindMeld, Streamer, Dare, OverlayView) are NOT
// routed through here. They carry their own per-page state machines and exist
// only while their page is mounted; folding them in is a separate refactor.

const WsContext = createContext(null);

export function WsProvider({ children }) {
  const { userInfo } = useSelector((state) => state.userLogin);
  const accessToken = userInfo?.access;

  // Keyed by message `type`; each entry is a Set of handlers. Refs, not state,
  // so subscribing never re-renders the provider or re-runs the socket effect.
  const handlersRef = useRef(new Map());
  // Fired every time a connection opens (including reconnects). Consumers that
  // need to resync state they may have missed while disconnected hang off this
  // rather than off a message type.
  const openHandlersRef = useRef(new Set());
  // The live socket, so `send` can reach it without re-running the effect (and
  // without exposing the socket itself, which consumers must not close).
  const socketRef = useRef(null);

  const api = useMemo(() => {
    const subscribe = (type, handler) => {
      const map = handlersRef.current;
      if (!map.has(type)) map.set(type, new Set());
      map.get(type).add(handler);
      return () => {
        const set = map.get(type);
        if (!set) return;
        set.delete(handler);
        if (set.size === 0) map.delete(type);
      };
    };

    const subscribeOpen = (handler) => {
      openHandlersRef.current.add(handler);
      return () => { openHandlersRef.current.delete(handler); };
    };

    // Emit a frame on the shared socket. Returns false when the socket is not
    // open, so callers can decide whether to retry; it never throws and never
    // buffers, because a queued subscribe replayed after a reconnect would be
    // stale by the time it flushed.
    //
    // Room membership is per-CONNECTION, not per-user: a reconnect drops every
    // subscription the backend was tracking. Consumers that subscribe to a room
    // must therefore re-send inside a `subscribeOpen` handler, not once on
    // mount, or their room goes silent after the first network blip.
    const send = (payload) => {
      const ws = socketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      try {
        ws.send(JSON.stringify(payload));
        return true;
      } catch {
        return false;
      }
    };

    return { subscribe, subscribeOpen, send };
  }, []);

  // Key on the access token so avatar/follower-count updates don't tear down
  // and reconnect the WS (visible as a brief unread-count flicker).
  useEffect(() => {
    if (!accessToken) return undefined;
    let ws = null;
    let reconnectDelay = 1000;
    let reconnectTimer = null;
    let unmounted = false;

    let failedOpens = 0;

    const connect = () => {
      if (unmounted) return;
      // Stop reconnecting after 5 consecutive failures (likely expired token)
      if (failedOpens >= 5) return;

      const wsHost = DOMAIN.replace(/^http/, 'ws');
      let opened = false;
      ws = new WebSocket(`${wsHost}/ws?token=${accessToken}`);
      socketRef.current = ws;

      ws.onopen = () => {
        opened = true;
        failedOpens = 0;
        reconnectDelay = 1000;
        // Let consumers resync whatever they may have missed while
        // disconnected (the bell re-fetches its unread count here, and room
        // consumers re-send their subscribe frames). socketRef is already
        // pointing at this socket, so a handler calling send() here reaches
        // the connection that just opened.
        openHandlersRef.current.forEach((handler) => {
          try { handler(); } catch { /* one consumer must not break the rest */ }
        });
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (!msg || !msg.type) return;
          const set = handlersRef.current.get(msg.type);
          if (!set) return;
          set.forEach((handler) => {
            try { handler(msg); } catch { /* isolate consumer errors */ }
          });
        } catch { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        // Drop the ref before anything else so a send() racing the close
        // returns false rather than throwing on a CLOSING socket.
        if (socketRef.current === ws) socketRef.current = null;
        if (unmounted) return;
        if (!opened) failedOpens += 1;
        reconnectTimer = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 2, 30000);
          connect();
        }, reconnectDelay);
      };

      ws.onerror = () => { ws.close(); };
    };

    const checkAndConnect = () => {
      failedOpens = 0;
      reconnectDelay = 1000;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        connect();
      }
    };

    connect();

    // bfcache: close WS on pagehide so the page stays eligible for the
    // back-forward cache; reopen on pageshow when restored from bfcache.
    const onPageHide = () => { if (ws) ws.close(); };
    const onPageShow = (e) => {
      if (e.persisted || !ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        checkAndConnect();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndConnect();
      }
    };
    const onOnline = () => {
      checkAndConnect();
    };

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', onOnline);
    window.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      unmounted = true;
      socketRef.current = null;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) ws.close();
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [accessToken]);

  return <WsContext.Provider value={api}>{children}</WsContext.Provider>;
}

WsProvider.propTypes = {
  children: PropTypes.node,
};

// Returns { subscribe, subscribeOpen, send }, or null when rendered outside the
// provider. Consumers must tolerate null so a component can be unit-tested
// or rendered in isolation without the whole app shell.
export const useWs = () => useContext(WsContext);

export default WsContext;
